import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SearchComponent from '../../components/ui/SearchComponent';
import PathwayHeader from './components/PathwayHeader';
import ModuleCard from './components/ModuleCard';
import RecommendedResources from './components/RecommendedResources';
import DiscussionForum from './components/DiscussionForum';
import UpcomingWorkshops from './components/UpcomingWorkshops';
import CertificationTracker from './components/CertificationTracker';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

const LearningPathwayPortal = () => {
  const navigate = useNavigate();
  const [currentUser] = useState({
    name: 'Alex Johnson',
    role: 'student',
    studentRole: 'Developer'
  });
  const [selectedRole, setSelectedRole] = useState('Developer');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/student-dashboard' },
    { label: 'Learning Pathway Portal' }
  ];

  const roleOptions = [
    { value: 'Developer', label: 'Developer' },
    { value: 'Project Manager', label: 'Project Manager' },
    { value: 'Business Analyst', label: 'Business Analyst' },
    { value: 'Product Designer', label: 'Product Designer' }
  ];

  const difficultyOptions = [
    { value: 'all', label: 'All Levels' },
    { value: 'Beginner', label: 'Beginner' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Advanced', label: 'Advanced' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Modules' },
    { value: 'available', label: 'Available' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'locked', label: 'Locked' }
  ];

  // Mock learning modules data
  const learningModules = [
    {
      id: 1,
      title: "IoT Fundamentals for Smart Cities",
      description: "Learn the basics of Internet of Things technology and its applications in urban environments. Understand sensor networks, data collection, and communication protocols.",
      icon: "Cpu",
      difficulty: "Beginner",
      duration: "4 hours",
      status: "completed",
      progress: 100,
      objectives: [
        "Understand IoT architecture and components",
        "Learn about sensor types and applications",
        "Master communication protocols (MQTT, HTTP)",
        "Implement basic IoT data collection"
      ],
      prerequisites: [],
      skills: ["IoT", "Sensors", "MQTT", "Data Collection"],
      role: "Developer"
    },
    {
      id: 2,
      title: "Smart City Data Analytics",
      description: "Explore data science techniques for analyzing urban data. Learn statistical methods, data visualization, and predictive modeling for city planning.",
      icon: "BarChart3",
      difficulty: "Intermediate",
      duration: "6 hours",
      status: "in-progress",
      progress: 65,
      objectives: [
        "Master statistical analysis techniques",
        "Create compelling data visualizations",
        "Build predictive models for urban planning",
        "Understand data privacy and ethics"
      ],
      prerequisites: ["IoT Fundamentals"],
      skills: ["Python", "Statistics", "Visualization", "Machine Learning"],
      role: "Developer"
    },
    {
      id: 3,
      title: "GIS and Spatial Analysis",
      description: "Master Geographic Information Systems for urban planning and analysis. Learn mapping techniques, spatial data processing, and location-based services.",
      icon: "Map",
      difficulty: "Intermediate",
      duration: "5 hours",
      status: "available",
      progress: 0,
      objectives: [
        "Understand GIS fundamentals and applications",
        "Learn spatial data types and formats",
        "Master mapping and visualization techniques",
        "Implement location-based analysis"
      ],
      prerequisites: ["Data Analytics Basics"],
      skills: ["GIS", "Mapping", "Spatial Analysis", "QGIS"],
      role: "Developer"
    },
    {
      id: 4,
      title: "AI in Urban Systems",
      description: "Discover how artificial intelligence is transforming smart cities. Learn machine learning applications in transportation, energy, and public services.",
      icon: "Brain",
      difficulty: "Advanced",
      duration: "8 hours",
      status: "locked",
      progress: 0,
      objectives: [
        "Understand AI applications in smart cities",
        "Learn machine learning algorithms",
        "Implement predictive systems",
        "Explore computer vision for urban monitoring"
      ],
      prerequisites: ["Data Analytics", "Python Programming"],
      skills: ["AI", "Machine Learning", "Computer Vision", "TensorFlow"],
      role: "Developer"
    },
    {
      id: 5,
      title: "Project Management for Smart Cities",
      description: "Learn project management methodologies specifically for smart city initiatives. Master agile practices, stakeholder management, and risk assessment.",
      icon: "Users",
      difficulty: "Beginner",
      duration: "3 hours",
      status: "completed",
      progress: 100,
      objectives: [
        "Master agile project management",
        "Learn stakeholder engagement strategies",
        "Understand risk management in tech projects",
        "Develop communication and leadership skills"
      ],
      prerequisites: [],
      skills: ["Agile", "Scrum", "Leadership", "Communication"],
      role: "Project Manager"
    },
    {
      id: 6,
      title: "Smart City Business Models",
      description: "Analyze business models and economic frameworks for smart city projects. Learn ROI calculation, funding strategies, and sustainability planning.",
      icon: "TrendingUp",
      difficulty: "Intermediate",
      duration: "4 hours",
      status: "in-progress",
      progress: 40,
      objectives: [
        "Understand smart city economics",
        "Learn ROI calculation methods",
        "Explore funding and investment strategies",
        "Develop sustainable business models"
      ],
      prerequisites: ["Project Management Basics"],
      skills: ["Business Analysis", "ROI", "Finance", "Strategy"],
      role: "Business Analyst"
    },
    {
      id: 7,
      title: "User Experience for Smart Cities",
      description: "Design user-centered experiences for smart city applications. Learn UX research, prototyping, and accessibility principles for urban technology.",
      icon: "Palette",
      difficulty: "Beginner",
      duration: "5 hours",
      status: "available",
      progress: 0,
      objectives: [
        "Master UX research methodologies",
        "Learn prototyping and wireframing",
        "Understand accessibility in urban tech",
        "Design inclusive user interfaces"
      ],
      prerequisites: [],
      skills: ["UX Design", "Prototyping", "Accessibility", "Figma"],
      role: "Product Designer"
    },
    {
      id: 8,
      title: "Sustainable Urban Design",
      description: "Explore sustainable design principles for smart city infrastructure. Learn about green technology, energy efficiency, and environmental impact assessment.",
      icon: "Leaf",
      difficulty: "Intermediate",
      duration: "6 hours",
      status: "available",
      progress: 0,
      objectives: [
        "Understand sustainable design principles",
        "Learn about green technology solutions",
        "Master energy efficiency analysis",
        "Conduct environmental impact assessments"
      ],
      prerequisites: ["Urban Planning Basics"],
      skills: ["Sustainability", "Green Tech", "Energy", "Environment"],
      role: "Product Designer"
    }
  ];

  // Filter modules based on selected role and filters
  const filteredModules = learningModules?.filter(module => {
    const matchesRole = selectedRole === 'all' || module.role === selectedRole;
    const matchesSearch = !searchQuery || 
      module.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      module.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      module.skills?.some(skill => skill?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    const matchesDifficulty = filterDifficulty === 'all' || module.difficulty === filterDifficulty;
    const matchesStatus = filterStatus === 'all' || module.status === filterStatus;
    
    return matchesRole && matchesSearch && matchesDifficulty && matchesStatus;
  });

  // Calculate progress statistics
  const totalModules = learningModules?.filter(m => m?.role === selectedRole)?.length;
  const completedModules = learningModules?.filter(m => m?.role === selectedRole && m?.status === 'completed')?.length;
  const completionPercentage = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleStartModule = (module) => {
    console.log('Starting module:', module.title);
    // Navigate to module content or update status
  };

  const handleContinueModule = (module) => {
    console.log('Continuing module:', module.title);
    // Navigate to module content
  };

  const handleViewModule = (module) => {
    console.log('Viewing completed module:', module.title);
    // Navigate to module review
  };

  const handleViewCertifications = () => {
    console.log('Viewing certifications');
  };

  const handleViewProgress = () => {
    console.log('Viewing detailed progress');
  };

  const handleResourceClick = (resource) => {
    console.log('Opening resource:', resource?.title);
    window.open(resource?.url, '_blank');
  };

  const handleViewFullForum = () => {
    navigate('/communication-hub');
  };

  const handleRegisterWorkshop = (workshop) => {
    console.log('Registering for workshop:', workshop?.title);
  };

  const handleViewAllWorkshops = () => {
    console.log('Viewing all workshops');
  };

  const handleViewCertificate = (cert) => {
    console.log('Viewing certificate:', cert?.title);
  };

  const handleShareAchievement = (cert) => {
    console.log('Sharing achievement:', cert?.title);
  };

  useEffect(() => {
    // Set initial role from user data
    if (currentUser?.studentRole) {
      setSelectedRole(currentUser?.studentRole);
    }
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole={currentUser?.role} 
        userName={currentUser?.name}
        notifications={3}
      />
      <main className="container mx-auto px-4 py-6">
        <Breadcrumb items={breadcrumbItems} />
        
        <PathwayHeader
          userRole={selectedRole}
          completionPercentage={completionPercentage}
          totalModules={totalModules}
          completedModules={completedModules}
          onViewCertifications={handleViewCertifications}
          onViewProgress={handleViewProgress}
        />

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {/* Filters and Search */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Select
                    label="Learning Path"
                    options={roleOptions}
                    value={selectedRole}
                    onChange={setSelectedRole}
                    className="w-full sm:w-48"
                  />
                  <Select
                    label="Difficulty"
                    options={difficultyOptions}
                    value={filterDifficulty}
                    onChange={setFilterDifficulty}
                    className="w-full sm:w-40"
                  />
                  <Select
                    label="Status"
                    options={statusOptions}
                    value={filterStatus}
                    onChange={setFilterStatus}
                    className="w-full sm:w-40"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <SearchComponent
                    placeholder="Search modules, skills..."
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    searchScope="learning"
                    className="w-full sm:w-64"
                  />
                  <div className="flex items-center space-x-1 border border-border rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="icon"
                      onClick={() => setViewMode('grid')}
                      className="h-8 w-8"
                    >
                      <Icon name="Grid3X3" size={16} />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="icon"
                      onClick={() => setViewMode('list')}
                      className="h-8 w-8"
                    >
                      <Icon name="List" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Learning Modules */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">
                  {selectedRole} Learning Modules
                </h2>
                <div className="text-sm text-muted-foreground">
                  {filteredModules?.length} of {totalModules} modules
                </div>
              </div>

              {filteredModules?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-8 text-center">
                  <Icon name="BookOpen" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No modules found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or search terms to find relevant learning content.
                  </p>
                  <Button variant="outline" onClick={() => {
                    setSearchQuery('');
                    setFilterDifficulty('all');
                    setFilterStatus('all');
                  }}>
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className={`grid gap-6 ${
                  viewMode === 'grid' ?'grid-cols-1 lg:grid-cols-2' :'grid-cols-1'
                }`}>
                  {filteredModules?.map((module) => (
                    <ModuleCard
                      key={module.id}
                      module={module}
                      onStartModule={handleStartModule}
                      onContinueModule={handleContinueModule}
                      onViewModule={handleViewModule}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <CertificationTracker
              userRole={selectedRole}
              onViewCertificate={handleViewCertificate}
              onShareAchievement={handleShareAchievement}
            />
            
            <RecommendedResources
              userRole={selectedRole}
              onResourceClick={handleResourceClick}
            />
            
            <UpcomingWorkshops
              onRegisterWorkshop={handleRegisterWorkshop}
              onViewAllWorkshops={handleViewAllWorkshops}
            />
            
            <DiscussionForum
              onViewFullForum={handleViewFullForum}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default LearningPathwayPortal;